import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {ApiResponse} from '../models/api-response.model';
import {
    DEFAULT_PAGINATION_META,
    loadPaginationPrefs,
    PaginatedState,
    PaginationMeta,
    PaginationParams,
    savePaginationPrefs,
} from '../models/pagination.model';

export interface HasId {
    id?: number;
}

export interface PaginatedStateWithSearch<T> extends PaginatedState<T> {
    search: string;
    searching: boolean;
}

export interface PaginatedSliceConfig<T extends HasId> {
    name: string;
    fetchFn: (params: PaginationParams) => Promise<ApiResponse<T[]>>;
    defaultSortBy?: string;
}

export function createPaginatedSlice<T extends HasId>(config: PaginatedSliceConfig<T>) {
    const {name, fetchFn, defaultSortBy = 'id'} = config;

    const prefs = loadPaginationPrefs(name);

    const initialState: PaginatedStateWithSearch<T> = {
        items: [],
        pagination: {...DEFAULT_PAGINATION_META, pageSize: prefs.pageSize},
        sort: {sortBy: prefs.sortBy || defaultSortBy, sortDirection: prefs.sortDirection},
        loading: false,
        searching: false,
        error: null,
        stale: false,
        search: '',
    };

    const fetchPage = createAsyncThunk(
        `${name}/fetchPage`,
        async (params: PaginationParams, {rejectWithValue}) => {
            try {
                const response = await fetchFn(params);
                return response;
            } catch (error: any) {
                return rejectWithValue(error?.message || 'Failed to fetch data');
            }
        }
    );

    const slice = createSlice({
        name,
        initialState,
        reducers: {
            setPage(state, action: PayloadAction<number>) {
                state.pagination.pageNumber = action.payload;
                state.stale = true;
            },
            setPageSize(state, action: PayloadAction<number>) {
                state.pagination.pageSize = action.payload;
                state.pagination.pageNumber = 0;
                state.stale = true;
                savePaginationPrefs(name, {
                    pageSize: action.payload,
                    sortBy: state.sort.sortBy,
                    sortDirection: state.sort.sortDirection,
                });
            },
            setSort(state, action: PayloadAction<{ sortBy: string; sortDirection: 'ASC' | 'DESC' }>) {
                state.sort = action.payload;
                state.pagination.pageNumber = 0;
                state.stale = true;
                savePaginationPrefs(name, {
                    pageSize: state.pagination.pageSize,
                    sortBy: action.payload.sortBy,
                    sortDirection: action.payload.sortDirection,
                });
            },
            setSearch(state, action: PayloadAction<string>) {
                state.search = action.payload;
                state.pagination.pageNumber = 0;
                state.stale = true;
            },
            markStale(state) {
                state.stale = true;
            },
            addItem(state, action: PayloadAction<T>) {
                const item = action.payload;
                state.items.unshift(item as any);
                if (state.items.length > state.pagination.pageSize) {
                    state.items.pop();
                }
                state.pagination.totalRecords += 1;
                state.pagination.totalPageItems = Math.min(
                    state.items.length,
                    state.pagination.pageSize
                );
                state.pagination.totalPages = Math.ceil(
                    state.pagination.totalRecords / state.pagination.pageSize
                );
            },
            updateItem(state, action: PayloadAction<T>) {
                const updated = action.payload;
                const index = state.items.findIndex((item: any) => item.id === updated.id);
                if (index !== -1) {
                    state.items[index] = updated as any;
                }
            },
            removeItem(state, action: PayloadAction<number>) {
                const id = action.payload;
                state.items = state.items.filter((item: any) => item.id !== id) as any;
                state.pagination.totalRecords = Math.max(0, state.pagination.totalRecords - 1);
                state.pagination.totalPageItems = state.items.length;
                state.pagination.totalPages = Math.ceil(
                    state.pagination.totalRecords / state.pagination.pageSize
                );
                if (state.items.length === 0 && state.pagination.pageNumber > 0) {
                    state.pagination.pageNumber -= 1;
                    state.stale = true;
                } else if (
                    state.items.length < state.pagination.pageSize &&
                    state.pagination.pageNumber < state.pagination.totalPages - 1
                ) {
                    state.stale = true;
                }
            },
            resetState() {
                return initialState;
            },
        },
        extraReducers: (builder) => {
            builder
                .addCase(fetchPage.pending, (state) => {
                    if (state.items.length === 0) {
                        state.loading = true;
                    } else {
                        state.searching = true;
                    }
                    state.error = null;
                })
                .addCase(fetchPage.fulfilled, (state, action) => {
                    const response = action.payload;
                    state.items = (response.body || []) as any;
                    if (response.pagination) {
                        state.pagination = response.pagination;
                    }
                    state.loading = false;
                    state.searching = false;
                    state.stale = false;
                    state.error = null;
                })
                .addCase(fetchPage.rejected, (state, action) => {
                    state.loading = false;
                    state.searching = false;
                    state.error = (action.payload as string) || 'Failed to fetch data';
                });
        },
    });

    return {
        slice,
        actions: {...slice.actions, fetchPage},
        reducer: slice.reducer,
    };
}
